/**
 * Taak Archiveer Dialog (Epic C-1 — Fundament, Story C-1.3, UX-C2)
 *
 * Confirm-dialog, zelfde custom-modal-patroon als `RejectDialog.tsx`
 * (overlay + Escape-to-close, geen shadcn/ui-dialog).
 *
 * De tellings-waarschuwing is bewust NEUTRAAL (geen amber/rood) — dit is
 * geen fout of kwalificatie-discretie-waarschuwing (die kleuren zijn
 * gereserveerd, build_constraints.visual_language); puur informatief, met
 * een icoon (nooit-alleen-kleur).
 */
import { useEffect } from 'react';
import { useArchiveTask } from '../hooks/usePlanningTasks';
import type { PlanningTaskDTO } from '@functieloonhuis/shared';

interface TaakArchiveerDialogProps {
  isOpen: boolean;
  onClose: () => void;
  task: PlanningTaskDTO | null;
}

export function TaakArchiveerDialog({ isOpen, onClose, task }: TaakArchiveerDialogProps) {
  const archiveTask = useArchiveTask();

  useEffect(() => {
    if (!isOpen) return;
    const listener = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !archiveTask.isPending) onClose();
    };
    document.addEventListener('keydown', listener);
    return () => document.removeEventListener('keydown', listener);
  }, [isOpen, onClose, archiveTask.isPending]);

  if (!isOpen || !task) return null;

  async function handleConfirm() {
    if (archiveTask.isPending || !task) return;
    await archiveTask.mutateAsync(task.id);
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" data-testid="taak-archiveer-dialog-overlay">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl" data-testid="taak-archiveer-dialog">
        <h2 className="mb-4 text-lg font-bold text-[#1E293B]">Taak archiveren</h2>

        <p className="text-sm text-gray-700" data-testid="taak-archiveer-copy">
          Taak &apos;{task.name}&apos; archiveren? Bestaande indelingen blijven zichtbaar in historische weergaven; nieuwe
          indelingen op deze taak zijn niet meer mogelijk.
        </p>

        {task.upcomingAssignmentCount > 0 && (
          <p
            data-testid="taak-archiveer-telling-waarschuwing"
            className="mt-3 flex items-start gap-2 rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-700"
          >
            <span aria-hidden="true">ℹ</span>
            <span>Let op: {task.upcomingAssignmentCount} toekomstige indelingen op deze taak blijven staan.</span>
          </p>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            data-testid="taak-archiveer-cancel"
            onClick={onClose}
            disabled={archiveTask.isPending}
            className="rounded-md border border-[#E2E8F0] px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Annuleren
          </button>
          <button
            type="button"
            data-testid="taak-archiveer-confirm"
            onClick={handleConfirm}
            disabled={archiveTask.isPending}
            className="rounded-md bg-[#2F5A7A] px-4 py-2 text-sm font-medium text-white hover:bg-[#2F5A7A]/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {archiveTask.isPending ? 'Bezig...' : 'Archiveren'}
          </button>
        </div>
      </div>
    </div>
  );
}
